import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { filter, switchMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
/**
 * @description WebSocketService is a service that connects to the K16 websocket API. This is the service we want to use going forward.
 * @category Services
 */
export class WebSocketService {
  private socket: WebSocket | null;
  private messageSubject = new Subject<any>();
  public messages = this.messageSubject.asObservable();
  private reconnectInterval: number = 5000;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts = 10;
  private userId;
  private isListening;
  private heartbeatInterval: number = 8 * 60 * 1000; // 8 minutes
  private heartbeatTimer: any;

  constructor(private userService: UserService) {
    // Add event listener for beforeunload to handle browser close or refresh
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
  }

  private handleBeforeUnload(event: Event): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        // Send details to removed cached connection from the database
        const customData = JSON.stringify({
          subdomain: environment.domain,
          user_id: this.userId,
        });
        // WebSocket close reason has a max length of 123 bytes
        const truncatedReason =
          customData.length > 123 ? customData.substring(0, 123) : customData;
        // Close the WebSocket connection
        this.socket.close(1000, truncatedReason);
      } catch (e) {
        // If close fails, ignore (browser might be closing)
        console.warn('Failed to close WebSocket on beforeunload:', e);
      }
    }
  }

  private getSocketUrl() {
    let url = environment.socketUrl;
    if (!/^wss:\/\//gi.test(url)) {
      url = 'wss://' + url;
    }
    return url;
  }

  private createSocket() {
    let queryPath = '?user-pool-id=' + environment.cognitoUserPoolId;
    queryPath += '&client-id=' + environment.cognitoClientId;
    queryPath += '&Authorization=' + this.userService.accessToken;
    return new WebSocket(this.getSocketUrl() + queryPath);
  }

  public connect(): Observable<boolean> {
    console.log('connect socket');
    let connected = new BehaviorSubject<boolean>(false);
    // wait for login before trying to connect
    this.userService.getLogInState().subscribe(loggedIn => {
      if (loggedIn && this.userService.accessToken) {
        this.userId = this.userService?.['_roleInformation']?.user_id;
        if (this.socket) return;
        if (!this.userId) this.handleReconnect();
        this.socket = this.createSocket();
        this.socket.onopen = event => {
          if (this.socket.readyState)
            this.socket.send(
              JSON.stringify({
                action: 'setlistener',
                data: {
                  type: 'data',
                  dataId: this.userId,
                  'user-pool-id': environment.cognitoUserPoolId,
                  'client-id': environment.cognitoClientId,
                  'sub-domain': environment.domain,
                },
              })
            );
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          connected.next(true);
        };

        this.socket.onmessage = event => {
          let message;
          try {
            const data = event.data.replace(/'/g, '"');
            message = JSON.parse(data);
          } catch (error) {}
          if (message?.['Status'] === 'Listener set.') {
            return (this.isListening = true);
          }

          if (message) {
            this.messageSubject.next(message);
          }
        };

        this.socket.onclose = event => {
          this.isListening = false;
          this.socket = null;
          this.stopHeartbeat();
          this.handleReconnect();
        };

        this.socket.onerror = error => {
          if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            try {
              const reason = JSON.stringify({
                subdomain: environment.domain,
                user_id: this.userId,
              });
              // WebSocket close reason has a max length of 123 bytes
              const truncatedReason =
                reason.length > 123 ? reason.substring(0, 123) : reason;
              this.socket.close(1002, truncatedReason);
            } catch (e) {
              // If close fails, just set socket to null
              console.warn('Failed to close WebSocket:', e);
            }
          }
          this.socket = null;
          this.stopHeartbeat();
          connected.next(false);
        };
      } else {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          try {
            const reason = JSON.stringify({
              subdomain: environment.domain,
              user_id: this.userId,
            });
            // WebSocket close reason has a max length of 123 bytes
            const truncatedReason =
              reason.length > 123 ? reason.substring(0, 123) : reason;
            this.socket.close(1000, truncatedReason);
          } catch (e) {
            // If close fails, just set socket to null
            console.warn('Failed to close WebSocket:', e);
          }
        }
        this.socket = null;
        this.stopHeartbeat();
        connected.next(false);
      }
    });
    return connected.asObservable();
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ data: { action: 'ping' } }));
      }
    }, this.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempt ${this.reconnectAttempts} to reconnect...`);
      timer(this.reconnectInterval).subscribe(() => {
        this.connect();
      });
    } else {
      console.error('Max reconnect attempts reached. Giving up.');
    }
  }

  sendMessage(message: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected.');
    }
  }

  subscribeToMessages(type?): Observable<any> {
    if (!this.socket) {
      this.connect().subscribe();
    }
    return type
      ? this.messages.pipe(filter(message => message?.type === type))
      : this.messages;
  }
}
